import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Package, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Confirmation page shown after a successful purchase.
 * Supports two flows:
 *  1. Stripe checkout redirect → ?session_id=cs_xxx (poll until paid).
 *  2. Wallet payment           → ?order_id=xxx (instantly resolved).
 */
export default function OrderSuccessPage() {
  const { language } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const orderIdParam = params.get('order_id');

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | paid | pending | failed
  const [error, setError] = useState('');

  const t = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  // Poll Stripe session up to 10 times (~30s) waiting for "paid".
  useEffect(() => {
    let mounted = true;

    const fetchByOrderId = async (id) => {
      try {
        const { data } = await axios.get(`${API}/orders/${id}`);
        if (!mounted) return;
        setOrder(data);
        setStatus(data.payment_status === 'paid' ? 'paid' : 'pending');
      } catch (e) {
        if (!mounted) return;
        setError(formatApiError(e, t('Neizdevās ielādēt pasūtījumu', 'Failed to load order', 'Не удалось загрузить заказ')));
        setStatus('failed');
      }
    };

    const pollSession = async (attempt = 0) => {
      if (!mounted) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          // Backend has linked the session to an order — fetch it
          try {
            const orders = await axios.get(`${API}/orders/by-session/${sessionId}`);
            if (orders.data?.id) {
              await fetchByOrderId(orders.data.id);
              return;
            }
          } catch {/* fall through */}
          setStatus('paid');
          return;
        }
        if (attempt < 10) {
          setTimeout(() => pollSession(attempt + 1), 3000);
        } else {
          setStatus('pending');
        }
      } catch (e) {
        if (!mounted) return;
        setError(formatApiError(e, t('Maksājuma statusu nevarēja pārbaudīt', 'Could not verify payment status', 'Не удалось проверить статус платежа')));
        setStatus('failed');
      }
    };

    if (orderIdParam) {
      fetchByOrderId(orderIdParam);
    } else if (sessionId) {
      pollSession();
    } else {
      setStatus('failed');
      setError(t('Trūkst pasūtījuma identifikatora', 'Missing order identifier', 'Отсутствует идентификатор заказа'));
    }
    return () => { mounted = false; };
  }, [sessionId, orderIdParam]);  // eslint-disable-line

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-10" data-testid="order-success-page">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-[#E5E4E0] p-8">
        {status === 'loading' && (
          <div className="flex flex-col items-center text-center py-8" data-testid="order-success-loading">
            <Loader2 className="w-12 h-12 text-[#D97736] animate-spin mb-4" />
            <h1 className="font-outfit text-xl font-semibold text-[#121212]">
              {t('Apstrādājam maksājumu...', 'Processing payment...', 'Обрабатываем платёж...')}
            </h1>
            <p className="text-sm text-[#5C5C5C] mt-2">
              {t('Lūdzu, nedrosies. Tas aizņems dažas sekundes.', 'Please wait. This will take a few seconds.', 'Пожалуйста, подождите. Это займёт несколько секунд.')}
            </p>
          </div>
        )}

        {status === 'paid' && (
          <div data-testid="order-success-paid">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h1 className="font-outfit text-2xl font-bold text-[#121212]">
                {t('Paldies par pirkumu!', 'Thank you for your purchase!', 'Спасибо за покупку!')}
              </h1>
              <p className="text-sm text-[#5C5C5C] mt-2">
                {t('Maksājums ir saņemts un pasūtījums nodots pārdevējam.', 'Payment received and your order has been forwarded to the seller.', 'Платёж получен, и заказ передан продавцу.')}
              </p>
            </div>

            {order && (
              <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5C5C]">{t('Pasūtījuma numurs', 'Order number', 'Номер заказа')}</span>
                  <span className="font-mono font-medium text-[#121212]">#{order.id?.slice(-8)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5C5C]">{t('Kopējā summa', 'Total', 'Итого')}</span>
                  <span className="font-semibold text-[#121212]">€{Number(order.total || 0).toFixed(2)}</span>
                </div>
                <div className="border-t border-[#E5E4E0] pt-3 space-y-1">
                  {(order.items || []).slice(0, 6).map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-[#121212] truncate mr-3">
                        {it.title} {it.quantity > 1 && <span className="text-[#5C5C5C]">×{it.quantity}</span>}
                      </span>
                      <span className="text-[#5C5C5C] flex-shrink-0">€{Number(it.subtotal || (it.price * (it.quantity || 1))).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {order.shipping_address && (order.shipping_address.address || order.shipping_address.name) && (
                  <div className="border-t border-[#E5E4E0] pt-3" data-testid="success-shipping-address">
                    <div className="text-xs font-medium text-[#4A6B53] mb-1">
                      {t('Piegādes adrese', 'Delivery address', 'Адрес доставки')}
                    </div>
                    <div className="text-sm text-[#121212] leading-snug">
                      {order.shipping_address.name && <span className="font-medium">{order.shipping_address.name}<br /></span>}
                      {order.shipping_address.address}
                      {order.shipping_address.postal_code ? `, ${order.shipping_address.postal_code}` : ''}
                      {order.shipping_address.phone && (
                        <span className="block text-[#5C5C5C]">{t('Tālr.', 'Tel.', 'Тел.')}: {order.shipping_address.phone}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link to="/orders" className="flex-1">
                <Button className="w-full bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="view-orders-btn">
                  <Package className="w-4 h-4 mr-2" />
                  {t('Mani pasūtījumi', 'My orders', 'Мои заказы')}
                </Button>
              </Link>
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full border-[#E5E4E0] text-[#5C5C5C]">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  {t('Turpināt iepirkties', 'Continue shopping', 'Продолжить покупки')}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="text-center py-4" data-testid="order-success-pending">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <Loader2 className="w-7 h-7 text-amber-600 animate-spin" />
            </div>
            <h1 className="font-outfit text-xl font-semibold text-[#121212]">
              {t('Maksājums vēl tiek apstrādāts', 'Payment is still being processed', 'Платёж ещё обрабатывается')}
            </h1>
            <p className="text-sm text-[#5C5C5C] mt-2">
              {t(
                'Tu drīz saņemsi apstiprinājumu uz e-pastu. Pasūtījumu redzēsi sadaļā "Mani pasūtījumi".',
                "You'll receive an email confirmation soon. The order will appear in 'My orders'.",
                'Вы скоро получите подтверждение на e-mail. Заказ появится в разделе «Мои заказы».',
              )}
            </p>
            <Link to="/orders" className="inline-block mt-5">
              <Button className="bg-[#D97736] hover:bg-[#C06528] text-white">
                {t('Skatīt manus pasūtījumus', 'View my orders', 'Посмотреть мои заказы')}
              </Button>
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-center py-4" data-testid="order-success-failed">
            <h1 className="font-outfit text-xl font-semibold text-red-700">
              {t('Maksājums neizdevās', 'Payment failed', 'Платёж не удался')}
            </h1>
            <p className="text-sm text-[#5C5C5C] mt-2">
              {error || t('Lūdzu, mēģini vēlreiz vai sazinies ar atbalstu.', 'Please try again or contact support.', 'Пожалуйста, попробуйте снова или свяжитесь с поддержкой.')}
            </p>
            <Link to="/cart" className="inline-block mt-5">
              <Button className="bg-[#D97736] hover:bg-[#C06528] text-white">
                {t('Atgriezties uz grozu', 'Back to cart', 'Вернуться в корзину')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
